import { useState, useCallback } from "react";

export const useAdmin = (db, { showAlert, showConfirm }) => {
    const [teachers, setTeachers] = useState([]);
    const [isAddingTeacher, setIsAddingTeacher] = useState(false);
    const [deletingTeacherUid, setDeletingTeacherUid] = useState(null);
    const [makingAdminUid, setMakingAdminUid] = useState("");
    const [editingTeacher, setEditingTeacher] = useState(null);
    const [savingTeacherUid, setSavingTeacherUid] = useState(null);

    const retrieveTeachers = useCallback(async () => {
        try {
            const [teacherList, adminUids] = await Promise.all([
                db.getTeachers(),
                db.getAdminUids().catch(() => []),
            ]);
            const adminSet = new Set(adminUids);
            const enriched = (teacherList || []).map((t) => ({
                ...t,
                isAdmin: adminSet.has(t.uid),
            }));
            setTeachers(enriched);
        } catch (error) {
            showAlert(
                "Error",
                "No se pudieron cargar los profesores: " + error.message,
            );
        }
    }, [db, showAlert]);

    const handleAddTeacher = useCallback(
        async ({ email, password, name, surname }) => {
            if (!email || !name || !surname || !password) {
                showAlert(
                    "Campos incompletos",
                    "Rellene todos los campos obligarios.",
                );
                return;
            }

            setIsAddingTeacher(true);

            try {
                await db.registerTeacher({ email, password, name, surname });
                await retrieveTeachers();
            } catch (error) {
                showAlert(
                    "Error",
                    "No se pudo registrar al profesor. " + error.message,
                );
            } finally {
                setIsAddingTeacher(false);
            }
        },
        [db, retrieveTeachers, showAlert],
    );

    const handleDeleteTeacher = useCallback(
        (teacherEmail) => {
            showConfirm({
                title: "Eliminar profesor",
                message: `¿Estas seguro que quieres eliminar a ${teacherEmail}? Esta acción no se puede deshacer`,
                danger: true,
                onConfirm: async () => {
                    setDeletingTeacherUid(teacherEmail);
                    try {
                        await db.deleteTeacher(teacherEmail);
                        setTeachers((prev) =>
                            prev.filter((t) => t.email !== teacherEmail),
                        );
                    } catch (error) {
                        throw new Error(error);
                    } finally {
                        setDeletingTeacherUid(null);
                    }
                },
            });
        },
        [db, showAlert, showConfirm],
    );

    const onMakeAdmin = useCallback(
        (teacherUid, teacherEmail) => {
            showConfirm({
                title: "Hacer administrador",
                message: `¿Estás seguro que quieres hacer a ${teacherEmail} administrador?`,
                danger: false,
                onConfirm: async () => {
                    setMakingAdminUid(teacherUid);
                    try {
                        await db.apiRequest(
                            `/api/teachers/${teacherUid}/admin`,
                            {
                                method: "POST",
                            },
                        );
                    } catch (error) {
                        showAlert(
                            "Error",
                            `Error haciendo al usuario administrador: ${error.message}`,
                        );
                    } finally {
                        await retrieveTeachers();
                        setMakingAdminUid("");
                    }
                },
            });
        },
        [db, showAlert, showConfirm],
    );

    const handleUpdateTeacher = useCallback(
        async (uid, { name, surname }) => {
            if (!name.trim() || !surname.trim()) {
                showAlert(
                    "Error",
                    "El nombre y apellidos no puede estar vacío",
                );
                return;
            }

            setSavingTeacherUid(uid);
            try {
                await db.apiRequest(`/api/teachers/${uid}`, {
                    method: "PUT",
                    body: JSON.stringify({
                        name: name.trim(),
                        surname: surname.trim(),
                    }),
                });
                setTeachers((prev) =>
                    prev.map((t) =>
                        t.uid === uid
                            ? {
                                  ...t,
                                  name: name.trim(),
                                  surname: surname.trim(),
                              }
                            : t,
                    ),
                );
                setEditingTeacher(null);
            } catch (error) {
                showAlert(
                    "Error",
                    `No se pudo actualizar el profesor: ${error.message}`,
                );
            } finally {
                setSavingTeacherUid(null);
            }
        },
        [db, showAlert],
    );

    return {
        teachers,
        isAddingTeacher,
        deletingTeacherUid,
        retrieveTeachers,
        handleAddTeacher,
        handleDeleteTeacher,
        onMakeAdmin,
        makingAdminUid,
        editingTeacher,
        setEditingTeacher,
        savingTeacherUid,
        handleUpdateTeacher,
    };
};
